import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/common';
import { Button, Input, Select, Textarea, Card, DatePicker } from '../components/ui';
import { REGIONS, CONTACT_INFO } from '../constants';
import { submitEstimate } from '../services/api';
import type { EstimateFormData } from '../types';
import './FreeEstimate.css';

/**
 * Free Estimate page component
 * Allows users to request a price estimate for their event
 */
const FreeEstimate: React.FC = () => {
	const { t } = useTranslation();
	const [formData, setFormData] = useState<EstimateFormData>({
		name: '',
		email: '',
		phone: '',
		eventType: '',
		guestCount: 10,
		preferredDate: '',
		region: '',
		additionalInfo: '',
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
	const [errorMessage, setErrorMessage] = useState<string>('');

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: name === 'guestCount' ? parseInt(value, 10) || 0 : value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setErrorMessage('');

		const result = await submitEstimate(formData);

		if (result.success) {
			setSubmitStatus('success');
		} else {
			setSubmitStatus('error');
			setErrorMessage(result.error || t('form.error'));
		}

		setIsSubmitting(false);
	};

	const eventTypeOptions = [
		{ value: 'birthday', label: t('form.eventTypes.birthday') },
		{ value: 'anniversary', label: t('form.eventTypes.anniversary') },
		{ value: 'graduation', label: t('form.eventTypes.graduation') },
		{ value: 'corporate', label: t('form.eventTypes.corporate') },
		{ value: 'holiday', label: t('form.eventTypes.holiday') },
		{ value: 'other', label: t('form.eventTypes.other') },
	];

	const regionOptions = REGIONS.map((region) => ({
		value: region.id,
		label: t(`nav.${region.id}`, region.name),
	}));

	if (submitStatus === 'success') {
		return (
			<div className="free-estimate">
				<SEO title={t('nav.freeEstimate')} />
				<div className="estimate-container">
					<Card variant="elevated" padding="lg" className="success-card">
						<div className="success-content">
							<span className="success-icon">✅</span>
							<h2>{t('form.success')}</h2>
							<p>{t('form.successMessage')}</p>
							<p className="contact-highlight">{CONTACT_INFO.phone}</p>
						</div>
					</Card>
				</div>
			</div>
		);
	}

	return (
		<div className="free-estimate">
			<SEO
				title={t('nav.freeEstimate')}
				description={t('estimate.subtitle')}
			/>

			<div className="estimate-container">
				<div className="estimate-header">
					<h1>{t('estimate.title')}</h1>
					<p>{t('estimate.subtitle')}</p>
				</div>

				<Card variant="elevated" padding="lg" className="estimate-form-card">
					<form className="estimate-form" onSubmit={handleSubmit}>
					<Input
						label={t('form.name')}
						name="name"
						value={formData.name}
						onChange={handleChange}
						placeholder={t('form.namePlaceholder') as string}
						required
					/>

						<div className="form-row">
							<Input
								label={t('form.email')}
								type="email"
								name="email"
								value={formData.email}
								onChange={handleChange}
								placeholder={t('form.emailPlaceholder', 'your@email.com') as string}
								required
							/>

							<Input
								label={t('form.phone')}
								type="tel"
								name="phone"
								value={formData.phone}
								onChange={handleChange}
								placeholder={t('form.phonePlaceholder', '(123) 456-7890') as string}
								required
							/>
						</div>

						<div className="form-row">
							<Select
								label={t('form.eventType')}
								name="eventType"
								value={formData.eventType}
								onChange={handleChange}
								options={eventTypeOptions}
								placeholder={t('form.selectOption')}
								required
							/>

						<Input
							label={t('form.guests')}
							type="number"
							name="guestCount"
							value={formData.guestCount.toString()}
							onChange={handleChange}
							min={10}
							hint={t('form.guestsHint') as string}
							required
						/>
						</div>

						<div className="form-row">
							<DatePicker
								label={t('form.date')}
								name="preferredDate"
								value={formData.preferredDate}
								onChange={handleChange}
								required
							/>

							<Select
								label={t('form.region')}
								name="region"
								value={formData.region}
								onChange={handleChange}
								options={regionOptions}
								placeholder={t('form.selectOption')}
								required
							/>
						</div>

					<Textarea
						label={t('form.message')}
						name="additionalInfo"
						value={formData.additionalInfo}
						onChange={handleChange}
						placeholder={t('form.messagePlaceholder') as string}
						rows={4}
					/>

						{submitStatus === 'error' && (
							<div className="form-status error">
								<p>{errorMessage || t('form.error')}</p>
							</div>
						)}

						<Button
							type="submit"
							variant="primary"
							size="lg"
							fullWidth
							loading={isSubmitting}
						>
							{isSubmitting ? t('form.submitting') : t('form.submit')}
						</Button>
					</form>
				</Card>

				<div className="estimate-contact">
					<p>
						{t('contact.phone')}: <a href={`tel:${CONTACT_INFO.phone}`}>{CONTACT_INFO.phone}</a>
					</p>
					<p>
						{t('contact.email')}: <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>
					</p>
				</div>
			</div>
		</div>
	);
};

export default FreeEstimate;
